/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Duration } from 'js-joda';
import _ from 'lodash';
import storeBackend from 'store';

import { ActiveAuth, AppState, core, dashboard } from 'types';
import { RoleType, toJSON } from '@tock/coretypes';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { TockProductLevel, TockProductType } from 'types/productFeatures';
import { configureRollbarForPatron } from '@tock/logging';
import { getActiveBusinessCookie, setActiveBusinessCookie } from 'utils/businessCookieUtils';
import { getAuthCookie, setAuthCookie } from '@tock/shared/utils/authCookieUtils';
import { getRoleType } from 'utils/roleUtils';
import { verifyCsrfToken } from '@tock/shared/utils/csrfTokenUtils';
import AppConstants from 'constants/AppConstants';
import AppSettings from 'settings/AppSettings';
import protos from 'utils/protos';
import supportedCountries from '@tock/shared/utils/supportedCountries';

const { AuthResponse } = protos;
const expirationInMs = Duration.ofDays(30).toMillis();

export type State = AppState;

export const defaultInitialState: AppState = {
  activeAuth: undefined,
  activeBusiness: undefined as any, // TODO: fix this.
  activeBusinessGroup: undefined,
  activeProductFeatureSet: undefined,
  authData: undefined,
  accountCreated: undefined,
  allBusinessGroups: [],
  allBusinessRoles: [],
  patron: {
    email: '',
  } as any,
  patronActivated: undefined,
  csrfToken: null,
  currency: 'USD',
  isAuthenticated: false,
  isAuthenticating: false,
  jwtToken: '',
  locale: 'en-us',
  locales: {},
  nativeAppVersion: '',
  osVersion: '',
  overrideLockout: false,
  patronRequestInProgress: false,
  timeZone: 'US/Central',
};

const initialState = generateInitialStateFromStorage(defaultInitialState);

function toBusinessId(businessRole: core.AuthBusinessResponse): number {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return businessRole.business!.id!;
}

function toBusinessName(businessRole: core.AuthBusinessResponse): string {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return businessRole.business!.name!.toUpperCase();
}

function toBusinessGroupId(businessGroup: core.BusinessGroup): number {
  return businessGroup.id;
}

function getAllBusinessRoles(authData: core.AuthResponse): core.AuthBusinessResponse[] {
  const businesRoles: core.AuthBusinessResponse[] = _.flatMap(
    authData.businessGroup as core.AuthBusinessGroupResponse[],
    (businessGroupRole) => {
      return businessGroupRole.business as core.AuthBusinessResponse[];
    }
  );
  return businesRoles.sort((a: core.AuthBusinessResponse, b: core.AuthBusinessResponse) => {
    return toBusinessName(a).localeCompare(toBusinessName(b));
  });
}

function getAllBusinessGroups(authData: core.AuthResponse): core.BusinessGroup[] {
  return _.map(authData.businessGroup, 'businessGroup') as core.BusinessGroup[];
}

function toAuthState(authData: core.AuthResponse): Partial<AppState> {
  return {
    allBusinessGroups: getAllBusinessGroups(authData),
    allBusinessRoles: getAllBusinessRoles(authData),
    authData,
    isAuthenticated: !!authData.token,
    isAuthenticating: false,
    jwtToken: authData.token,
  };
}

function restoreActiveAuth(
  authData: core.AuthResponse,
  prevActiveAuth?: ActiveAuth,
  currentActiveBusiness?: core.Business
) {
  const businessIds = _.map(getAllBusinessRoles(authData), toBusinessId);
  const activeBusinessCookie = getActiveBusinessCookie(AppSettings.API_BASE);
  let storeActiveBusinessId: any = null;
  if (activeBusinessCookie) {
    storeActiveBusinessId = activeBusinessCookie.id;
  }
  if (storeActiveBusinessId && _.includes(businessIds, storeActiveBusinessId)) {
    return activeAuth(authData, storeActiveBusinessId, currentActiveBusiness);
  } else if (prevActiveAuth && _.includes(businessIds, prevActiveAuth.businessId)) {
    return activeAuth(authData, prevActiveAuth.businessId, currentActiveBusiness);
  } else if (businessIds.length > 0) {
    // Just set to the first business in the list.
    return activeAuth(authData, businessIds[0], currentActiveBusiness);
  } else {
    return {};
  }
}

// Visible for testing.
export function clearAuthData() {
  setAuthCookie(AppSettings.API_BASE, '', 0);
  setActiveBusinessCookie(AppSettings.API_BASE, {}, 0);
  if (storeBackend.enabled) {
    storeBackend.remove('authData');
  }
}

function activeAuth(
  authData: core.AuthResponse | undefined,
  businessId: number,
  currentActiveBusiness?: core.Business
): Partial<AppState> {
  if (!authData || _.isEmpty(authData)) {
    return {};
  }

  const allBusinessRoles = getAllBusinessRoles(authData);
  const activeBusinessRole = _.find(
    allBusinessRoles,
    (business) => businessId === toBusinessId(business)
  );

  let activeBusiness = (currentActiveBusiness || activeBusinessRole?.business) as core.Business;
  if (!activeBusiness && allBusinessRoles.length > 0) {
    activeBusiness = allBusinessRoles[0].business as core.Business;
  }

  if (!activeBusiness || !activeBusinessRole) {
    console.error(
      `Couldn't activate business with id ${businessId} and no fallback exists. Bailing`
    );

    console.error(allBusinessRoles);
    clearAuthData();
    // see https://developer.mozilla.org/en-US/docs/Web/API/Location/reload#location.reload_has_no_parameter
    window.location.reload();
    return {};
  }

  const activeProductType = activeBusinessRole.tockProductType;
  const activeProductLevel = activeBusinessRole.tockProductLevel;
  const subscriptionAddons = activeBusiness.subscriptionAddon;
  const activeProductFeatureSet =
    activeProductType && activeProductLevel
      ? {
          productType: TockProductType[activeProductType],
          productLevel: TockProductLevel[activeProductLevel],
          subscriptionAddons,
        }
      : undefined;

  const allBusinessGroups = getAllBusinessGroups(authData);
  const activeBusinessGroup = _.find(allBusinessGroups, (group) => {
    return toBusinessGroupId(group) === activeBusiness?.businessGroupId;
  });

  const locale = activeBusiness.locale;
  const timeZone = activeBusiness.timeZone;
  const appState = {
    activeAuth: {
      businessGroupId: activeBusiness.businessGroupId,
      businessId: activeBusiness.id,
      roleType: getRoleType(authData.role, activeBusinessRole.role),
    },
    activeBusiness,
    activeBusinessGroup,
    activeProductFeatureSet,
    allBusinessRoles,
    allBusinessGroups,
    currency: activeBusiness?.currencyCode,
    locale,
    locales: _.mapValues(supportedCountries, (supportedCountry) => supportedCountry.locales),
    timeZone,
  };

  // TODO: Find a dependency injection way to pass this down to javascript libraries.
  (window as any).app = {
    currency: appState.currency,
    locale: appState.locale,
    timeZone: appState.timeZone,
  };
  return appState;
}

function authenticationRolesUpdate(state: AppState, authBusinessResponse: core.AuthResponse) {
  const authDataJson: core.AuthResponse = state.authData || ({} as any);
  const authData = authDataJson;
  authData.role = authBusinessResponse.role;
  authData.businessGroup = authBusinessResponse.businessGroup;
  safeSaveAuthData(authData, state.activeAuth);
  return {
    authData,
  };
}

function saveActiveAuthCookies(activeAuthData: Partial<AppState>) {
  const activeBusiness = _.pick(activeAuthData.activeBusiness, [
    'id',
    'businessGroupId',
    'currencyCode',
    'emailLogoUrl',
    'hasPosIntegration',
    'locale',
    'name',
    'timeZone',
  ]);
  const dataToSave = {
    ...activeBusiness,
    ..._.pick(activeAuthData.activeAuth, ['roleType']),
  };
  setActiveBusinessCookie(AppSettings.API_BASE, dataToSave, expirationInMs);
}

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type, payload } = action;
  switch (type) {
    case AppConstants.ACCOUNT_ACTIVATION_STARTING: {
      return {
        ...state,
        patronActivated: undefined,
      };
    }
    case AppConstants.ACCOUNT_ACTIVATION_DONE: {
      return {
        ...state,
        patronActivated: true,
      };
    }
    case AppConstants.ACCOUNT_ACTIVATION_FAILED: {
      return {
        ...state,
        patronActivated: false,
      };
    }
    case AppConstants.AUTHENTICATION_SET_ACTIVE_DONE: {
      const businessId: number = toJSON<number>(payload);
      const activeAuthData = activeAuth(state.authData, businessId);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      safeSaveAuthData(state.authData!, activeAuthData.activeAuth);
      saveActiveAuthCookies(activeAuthData);
      const csrfToken = verifyCsrfToken(state.csrfToken);
      return {
        ...state,
        ...activeAuthData,
        csrfToken,
      };
    }
    case AppConstants.AUTHENTICATION_RESPONSE_DONE: {
      const authData = toJSON<core.AuthResponse>(payload);
      if (!authData.token || !authData.businessGroup || authData.businessGroup.length === 0) {
        return {
          ...state,
          isAuthenticating: false,
        };
      }
      const newState = toAuthState(authData);
      const activeAuthData = restoreActiveAuth(authData, state.activeAuth, state.activeBusiness);
      safeSaveAuthData(authData, activeAuthData.activeAuth);
      setAuthCookie(AppSettings.API_BASE, authData.token, expirationInMs);
      saveActiveAuthCookies(activeAuthData);
      const csrfToken = verifyCsrfToken(authData.csrfToken);

      return {
        ...state,
        ...newState,
        ...activeAuthData,
        csrfToken,
      };
    }
    case AppConstants.AUTHENTICATION_RESPONSE_SELF_ONBOARDING_DONE: {
      const authData = toJSON<core.AuthResponse>(payload);
      const newState = toAuthState(authData);
      setAuthCookie(AppSettings.API_BASE, authData.token, expirationInMs);
      return {
        ...state,
        ...newState,
      };
    }
    case AppConstants.ONBOARDING_GET_AUTH_DONE: {
      const authData = toJSON<core.AuthResponse>(payload);
      const newState = toAuthState(authData);
      safeSaveAuthData(authData, newState.activeAuth);
      setAuthCookie(AppSettings.API_BASE, authData.token, expirationInMs);
      return {
        ...state,
        ...newState,
      };
    }
    case AppConstants.ONBOARDING_GET_BUSINESS_DONE: {
      const onboardingData = toJSON<dashboard.BusinessOnboarding>(payload);
      const newState = {
        patron: onboardingData.patron as core.Patron,
      };
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      configureRollbarForPatron(newState.patron, state.activeAuth!);

      return {
        ...state,
        ...newState,
      };
    }
    case AppConstants.AUTHENTICATION_ROLES_UPDATE_DONE: {
      const authBusinessResponse = toJSON<core.AuthResponse>(payload);
      const newState = authenticationRolesUpdate(state, authBusinessResponse);
      const activeAuth = restoreActiveAuth(
        newState.authData,
        state.activeAuth,
        state.activeBusiness
      );
      return {
        ...state,
        ...newState,
        ...activeAuth,
      };
    }
    case AppConstants.ONBOARDING_FETCH_NEW_ROLES_DONE: {
      const authBusinessResponse = toJSON<core.AuthResponse>(payload);
      const newState = authenticationRolesUpdate(state, authBusinessResponse);
      return {
        ...state,
        ...newState,
      };
    }
    case AppConstants.PATRON_FETCH_PROFILE_STARTING: {
      return {
        ...state,
        patronRequestInProgress: true,
      };
    }
    case AppConstants.PATRON_FETCH_PROFILE_DONE:
    case AppConstants.PATRON_SET_PROFILE_DONE: {
      const patron = toJSON<core.Patron>(payload);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      configureRollbarForPatron(patron, state.activeAuth!);

      return {
        ...state,
        patron,
        patronRequestInProgress: false,
      };
    }
    case AppConstants.AUTHENTICATION_CLEARED_DONE:
      clearAuthData();
      return {
        ...defaultInitialState,
      };

    case AppConstants.AUTHENTICATION_RESPONSE_STARTING:
    case AppConstants.ONBOARDING_GET_AUTH_STARTING:
      return {
        ...state,
        isAuthenticating: true,
      };
    case AppConstants.AUTHENTICATION_RESPONSE_FAILED:
    case AppConstants.ONBOARDING_GET_AUTH_FAILED:
      return {
        ...state,
        isAuthenticating: false,
      };
    case AppConstants.CREATE_ACCOUNT_DONE:
      return {
        ...state,
        accountCreated: true,
      };
    case AppConstants.CREATE_ACCOUNT_STARTING:
    case AppConstants.CREATE_ACCOUNT_FAILED:
      return {
        ...state,
        accountCreated: false,
      };

    case AppConstants.BUSINESS_FETCH_DONE:
    case AppConstants.BUSINESS_UPDATE_DONE:
    case AppConstants.CANCELLATION_POLICY_BUSINESS_UPDATE_DONE: {
      const business = toJSON<core.Business>(payload);
      if (business.id !== state.activeBusiness.id) {
        return state;
      }
      return {
        ...state,
        activeBusiness: business,
        locale: business.locale,
      };
    }
    case AppConstants.CANCELLATION_POLICY_BUSINESS_UPDATE_STARTING:
    case AppConstants.CANCELLATION_POLICY_BUSINESS_UPDATE_FAILED:
      return {
        ...state,
      };
    case AppConstants.COMMUNICATION_OPTIN_SET_REQUESTS_OPTIN_DONE:
      return {
        ...state,
        activeBusiness: {
          ...state.activeBusiness,
          requestEmailOptIn: payload,
        },
      };
    case AppConstants.OVERRIDE_LOCKOUT:
      return {
        ...state,
        overrideLockout: true,
      };
    case AppConstants.TOCK_BANK_ACCOUNT_VERIFY_DONE:
      return {
        ...state,
        activeBusiness: {
          ...state.activeBusiness,
          subscriptionState: 'OK',
        },
      };
    case AppConstants.AUTH_REFRESH_FROM_STORAGE:
      return {
        ...generateInitialStateFromStorage(state),
      };
    case AppConstants.GET_NATIVE_VERSIONS_DONE:
      return {
        ...state,
        osVersion: payload.osVersion,
        nativeAppVersion: payload.nativeAppVersion,
      };
    case AppConstants.ONLINE_WALKIN_WAITLIST_TOGGLE_DONE:
      return {
        ...state,
        activeBusiness: state.activeBusiness && {
          ...state.activeBusiness,
          onlineWalkinWaitlistEnabled: !!payload,
        },
      };

    case SharedAppConstants.AUTHENTICATION_CSRF_REFRESH_STARTING: {
      return {
        ...state,
      };
    }

    case SharedAppConstants.AUTHENTICATION_CSRF_REFRESH_FAILED: {
      return {
        ...state,
        csrfToken: null,
      };
    }

    case SharedAppConstants.AUTHENTICATION_CSRF_REFRESH_DONE: {
      const csrfToken = toJSON<core.CsrfToken>(payload);
      return {
        ...state,
        csrfToken,
      };
    }

    default:
      return state;
  }
}

function generateInitialStateFromStorage(defaultInitialState: AppState) {
  let generatedState = {
    ...defaultInitialState,
  };

  // If there is stored data in local storage, try to lean on that
  // This respects the currently set active business cookie if it exists
  const storedAuthData = storeBackend.enabled ? storeBackend.get('authData') : null;
  if (storedAuthData) {
    let parsedAuthData = AuthResponse.fromObject({});
    try {
      parsedAuthData = toJSON(AuthResponse.fromObject(JSON.parse(storedAuthData)));
    } catch (_error) {
      console.error('Failed to parse stored auth data');
      return generatedState;
    }
    generatedState = {
      ...generatedState,
      ...toAuthState(parsedAuthData),
      ...restoreActiveAuth(parsedAuthData),
    };
  }

  // Respect the auth cookie 100%. Let server decide to boot out people if the changed auth is incorrect.
  // If it's absent, clear the stored data and mark the state is isAuthenticated = false to trigger a log-out
  const existingAuthCookie = getAuthCookie(AppSettings.API_BASE);
  if (existingAuthCookie) {
    generatedState = {
      ...generatedState,
      jwtToken: existingAuthCookie,
      isAuthenticated: true,
    };
  } else if (!existingAuthCookie) {
    clearAuthData();
    generatedState.jwtToken = '';
    generatedState.isAuthenticated = false;
  }

  // If we are authenticated but don't have an active business (from local storage)
  // then initialize the state with the info necessary to boot up safely.
  // The rest of the auth data will be loaded asynchronously
  if (!_.isEmpty(generatedState.jwtToken) && _.isEmpty(generatedState.activeBusiness)) {
    const activeBusinessCookie = getActiveBusinessCookie(AppSettings.API_BASE);
    if (activeBusinessCookie && activeBusinessCookie.id) {
      generatedState.activeAuth = {
        businessId: activeBusinessCookie.id as number,
        businessGroupId: activeBusinessCookie.businessGroupId as number,
        // if the roleType isn't in the cookie, take it from the deprecated value of roles
        roleType: activeBusinessCookie.roleType || RoleType[activeBusinessCookie.roles?.[0]],
      };
      generatedState.activeBusiness = _.pick(activeBusinessCookie, [
        'id',
        'businessGroupId',
        'currencyCode',
        'locale',
        'name',
      ]) as any;
      if (activeBusinessCookie.businessGroupId) {
        generatedState.activeBusinessGroup = {
          id: activeBusinessCookie.businessGroupId as number,
        } as any;
      }
    }
  }
  return generatedState;
}

function safeSaveAuthData(authData: core.AuthResponse, activeAuth?: ActiveAuth) {
  if (!storeBackend.enabled) {
    console.warn('Caught local storage write error:');
    return;
  }
  const activeBusinessGroupId = activeAuth?.businessGroupId;
  if (!activeBusinessGroupId || !authData) {
    console.warn('Cannot write auth data because no active business group');
    return;
  }
  let authDataToSave = authData;
  authDataToSave = {
    ...authDataToSave,
    businessGroup: _.filter(authData.businessGroup, (businessGroupProto) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return businessGroupProto.businessGroup!.id === activeBusinessGroupId;
    }),
  };
  storeBackend.set('authData', JSON.stringify(authDataToSave));
}
